import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Flex, Box } from 'reflexbox'
import { push } from 'connected-react-router'

import { getRunbook, getRunbookFromServer } from 'src/store/modules/runbooks'
import { setActiveRunbook } from 'src/store/modules/ui/runbook'

import DirectedGraph from './DirectedGraph'
import DepthSelector from './DepthSelector'
import Legend from './Legend'
import ActionInfo from './ActionInfo'

import { subscribeToRunbook } from 'src/utils/websockets'
import { updateAction } from 'src/store/modules/runbooks/actions'
import {
    AnchorButton,
    Dialog,
    Classes,
    Button,
    Spinner,
    Icon,
} from '@blueprintjs/core'

import ElementsBuilder from './ElementsBuilder'

import { asyncUpdateRunbook } from 'src/store/modules/runbooks'
import {
    toggleSave,
    toggleReset,
    enableLevel,
} from 'src/store/modules/ui/runbookgraph'

import { scaleLinear } from 'd3-scale'

const isNew = (id) => id === 'new'
const getId = (id) => (isNew(id) ? null : id)

const sizeScale = scaleLinear().domain([1, 150]).range([40, 100])

class RunbookGraph extends React.Component {
    state = {
        clickedActionUid: null,
        clickedActionData: null,
    }

    static statusDetails = {
        0: {
            id: 0,
            name: 'Pending',
            color: '#aaa',
            details: 'New',
        },
        1: {
            id: 1,
            name: 'In Progress',
            color: '#dee260',
            details: 'Sending API call or email',
        },
        2: {
            id: 2,
            name: 'Waiting',
            color: '#24b7eb',
            details: 'Ready for execution when doing manual',
        },
        3: {
            id: 3,
            name: 'Failed to send',
            color: '#ee8933',
            details: 'API Unreachable, email cannot be sent',
        },
        4: {
            id: 4,
            name: 'Success',
            color: '#3ebb48',
            details:
                'Manually confirmed, API response OK, Positive email feedback',
        },
        5: {
            id: 5,
            name: 'Failed',
            color: '#d82f27',
            details:
                'API call failed, Negative email feedback, Manual confirmation not OK',
        },
    }

    connectToRunbookWebSocket(runbookId) {
        // socket.readyState === 3 means that the state of the connection is WebSocket.CLOSED
        if (
            this.socket === undefined ||
            (this.socket && this.socket.readyState === 3)
        ) {
            const { updateAction } = this.props.actions
            const { dispatch, websocketConfig } = this.props

            // Only connect if we have websocket config
            if (!websocketConfig) {
                console.error(
                    'WebSocket configuration not available, skipping connection'
                )
                return
            }

            this.socket = subscribeToRunbook(
                runbookId,
                (data) =>
                    updateAction(runbookId, data.actionUid, {
                        status: data.attrs.status,
                        startTime: data.attrs.startTime,
                        endTime: data.attrs.endTime,
                        ownerUser: data.attrs.ownerUser,
                        ownerTeam: data.attrs.ownerTeam,
                        ownerEmail: data.attrs.ownerEmail,
                    }),
                dispatch,
                websocketConfig
            )
        }
    }

    componentDidMount() {
        const { runbookId } = this.props
        const { setActiveRunbook, getRunbookFromServer } = this.props.actions

        setActiveRunbook(runbookId)
        if (runbookId) {
            getRunbookFromServer(runbookId)
            this.webSocketReconnectInterval = setInterval(
                () => this.connectToRunbookWebSocket(runbookId),
                10000
            )
        }
    }

    componentWillUnmount() {
        if (this.socket) {
            this.socket.close()
        }
        if (this.webSocketReconnectInterval) {
            clearInterval(this.webSocketReconnectInterval)
        }
    }

    handleRunbookClick = (e) => {
        const { runbookId } = this.props
        e.preventDefault()
        this.props.dispatch(push(`/runbooks/${runbookId}`))
    }

    handleSaveClick = (e) => {
        const { runbookId } = this.props
        this.props.actions.asyncUpdateRunbook(runbookId)
        this.props.actions.toggleSave()
    }

    handleResetbtnClick = (e) => {
        this.props.actions.toggleReset()
    }

    resetGraph = (e) => {
        const {
            runbook: { maxDepth },
        } = this.props
        const { actionsById, runbookId } = this.props
        const actions = actionsById[runbookId].actions.byId

        for (let i = 0; i <= maxDepth; i++) {
            this.props.actions.enableLevel(i)
        }

        for (const [key] of Object.entries(actions)) {
            this.props.actions.updateAction(runbookId, key, {
                positionX: null,
                positionY: null,
            })
        }

        this.props.actions.asyncUpdateRunbook(runbookId)
        this.props.actions.toggleReset()
    }

    getElementsFromRunbook() {
        const { graphLevel } = this.props
        const eb = new ElementsBuilder(this.props.runbook, graphLevel)
        return eb.getElements()
    }

    getGraphStyle() {
        const getPiePercentage = (status) => {
            return (ele) => {
                const data = ele.data()
                const count = data.statusCount[status]
                let perc = 0
                if (count !== undefined && count !== null) {
                    perc = (count / data.count) * 100
                }

                return `${perc}%`
            }
        }

        const withDevicesScale = (ele) => sizeScale(ele.data('count'))

        const solutionStyle = {
            selector: 'node[type="solution"]',
            style: {
                width: 40,
                height: 40,
                content: 'data(name)',
                'text-opacity': 0.5,
                'background-color': (ele) =>
                    RunbookGraph.statusDetails[ele.data('status')].color,
                color: '#000',
                'text-valign': 'top',
                'text-halign': 'left',
            },
        }

        let withDevicesStyle = {
            selector: 'node[type="devices"]',
            style: {
                width: withDevicesScale,
                height: withDevicesScale,
                content: 'data(name)',
                'text-opacity': 0.5,
                'text-valign': 'top',
                'text-halign': 'left',

                'pie-size': '100%',
            },
        }

        const statusIds = Object.keys(RunbookGraph.statusDetails)

        for (let i = 1; i < statusIds.length; i++) {
            const status = statusIds[i]
            withDevicesStyle.style[`pie-${i}-background-color`] =
                RunbookGraph.statusDetails[status].color
            withDevicesStyle.style[
                `pie-${i}-background-size`
            ] = getPiePercentage(status)
        }

        const edgeStyle = {
            selector: 'edge',
            style: {
                'curve-style': 'bezier',
                width: 4,
                'target-arrow-shape': 'triangle',
                'line-color': '#9dbaea',
                'target-arrow-color': '#9dbaea',
            },
        }

        return [solutionStyle, withDevicesStyle, edgeStyle]
    }

    renderDirectedGraph() {
        const {
            runbook,
            runbookId,
            isGraphSaved,
            isReset,
            runbookLoading,
        } = this.props
        const { toggleSave, toggleReset } = this.props.actions

        if (!runbook) {
            return null
        }

        const { uids } = runbook.actions
        if (!uids || uids.length === 0) {
            return null
        }

        const {
            runbook: { maxDepth },
        } = this.props
        const { clickedActionUid, clickedActionData } = this.state

        const { nodes, edges } = this.getElementsFromRunbook()

        if (runbookLoading === true) {
            return (
                <div className="runbook-graph">
                    <Spinner size={Icon.SIZE_BIG} />
                </div>
            )
        } else {
            return (
                <div className="runbook-graph">
                    <div className="right-side-controls">
                        <AnchorButton
                            icon="comparison"
                            text="View runbook"
                            onClick={this.handleRunbookClick}
                            href={`/runbooks/${runbookId}`}
                        />
                        <AnchorButton
                            icon="document"
                            text="Save runbook layout"
                            onClick={this.handleSaveClick}
                        />
                        <AnchorButton
                            icon="graph"
                            text="Reset graph layout"
                            onClick={this.handleResetbtnClick}
                        />
                        <DepthSelector maxDepth={maxDepth} />
                        <Legend
                            items={Object.values(RunbookGraph.statusDetails)}
                        />
                    </div>
                    {isGraphSaved ? (
                        <Dialog
                            onclose={toggleSave}
                            title="Runbook layout saved successfully"
                            icon="tick-circle"
                            isOpen={true}
                            canOutsideClickClose={false}
                            isCloseButtonShown={false}
                        >
                            <div className={Classes.DIALOG_BODY}>
                                The layout of this graph has been stored
                                succesfully. Upon reloading the graph, the graph
                                will be rendered as how it was saved.
                            </div>
                            <div className={Classes.DIALOG_FOOTER}>
                                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                                    <Button onClick={toggleSave}>Close</Button>
                                </div>
                            </div>
                        </Dialog>
                    ) : null}
                    {isReset ? (
                        <Dialog
                            onclose={toggleReset}
                            title="Resetting graph layout"
                            icon="warning-sign"
                            isOpen={true}
                            canOutsideClickClose={false}
                            isCloseButtonShown={false}
                        >
                            <div className={Classes.DIALOG_BODY}>
                                <p>
                                    <strong>
                                        Do you want to reset the graph layout?
                                    </strong>
                                </p>
                                <p>
                                    If you press yes, you cant retrieve the old
                                    layout anymore!
                                </p>
                            </div>
                            <div className={Classes.DIALOG_FOOTER}>
                                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                                    <Button onClick={toggleReset}>No</Button>
                                    <Button
                                        intent="primary"
                                        onClick={this.resetGraph}
                                    >
                                        Yes
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    ) : null}
                    <DirectedGraph
                        ref="graph"
                        nodes={nodes}
                        edges={edges}
                        runbookId={runbookId}
                        style={this.getGraphStyle()}
                        onNodeClick={(data) =>
                            this.setState({
                                clickedActionUid: data.id,
                                clickedActionData: data,
                            })
                        }
                    />
                    <div className="left-side-controls">
                        <ActionInfo
                            actionUid={clickedActionUid}
                            actionData={clickedActionData}
                        />
                    </div>
                </div>
            )
        }
    }

    render() {
        return (
            <Flex p={2} align="center">
                <Box px={2} w={1 / 100} />
                {this.renderDirectedGraph()}
                <Box px={2} w={1 / 100} />
            </Flex>
        )
    }
}

RunbookGraph.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    }),
}

const mapStateToProps = (state, ownProps) => {
    const runbookId = getId(ownProps.match.params.id)
    return {
        runbook: getRunbook(state, runbookId),
        runbookId,
        actionsById: state.runbooks.byId,
        isGraphSaved: state.ui.runbookgraph.isGraphSaved,
        isReset: state.ui.runbookgraph.isReset,
        runbookLoading: state.ui.runbook.runbookLoading,
        graphLevel: state.ui.runbookgraph.graphLevel,
        websocketConfig: state.app.websocketConfig,
    }
}

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        dispatch,
        actions: bindActionCreators(
            {
                getRunbookFromServer: () =>
                    getRunbookFromServer(ownProps.match.params.id),
                setActiveRunbook,
                updateAction,
                asyncUpdateRunbook,
                toggleSave,
                toggleReset,
                enableLevel,
            },
            dispatch
        ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(RunbookGraph)
